export interface ShoppingListItem {
  id: string
  name: string

  // NEW: Structured purchase guidance (Issue #38, #122)
  totalQuantity?: string        // Total quantity needed from recipes (e.g., "4.5 cups", "6 tbsp")
  retailQuantity?: string       // Purchasable retail unit from LLM (e.g., "1 bulb", "2 bunches") — source for amount/unit fields (#122)
  purchaseGuidance?: string     // Purchase recommendation (e.g., "1 bag (32 oz)", "Buy 2 lbs minimum")
  recipeBreakdown?: string[]    // Per-recipe breakdown (e.g., ["Thai Curry: 2 cups", "Pad Thai: 2.5 cups"])

  // DEPRECATED: Legacy fields kept for backward compatibility with stored lists
  amount: number
  unit: string

  category: StoreSection
  originalIngredients: OriginalIngredient[] // Track source recipes for debugging
  checked: boolean
  notes?: string
  instacartName?: string // Original detailed name for Instacart product matching (e.g., "chicken thighs, bone-in")
}

export interface OriginalIngredient {
  recipeId: string
  recipeName: string
  originalAmount: number
  originalUnit: string
}

export interface ShoppingList {
  id: string
  name: string
  items: ShoppingListItem[]
  mealPlanId?: string
  createdAt: string
  updatedAt: string
  completedAt?: string
  status: ShoppingListStatus
}

export type ShoppingListStatus = 
  | 'active'      // User is reviewing/editing
  | 'shopping'    // User is actively shopping
  | 'completed'   // User completed shopping
  | 'archived'    // Older shopping list

export enum StoreSection {
  PRODUCE = 'produce',
  MEAT = 'meat',
  SEAFOOD = 'seafood', 
  DAIRY = 'dairy',
  PANTRY = 'pantry',
  SPICES = 'spices',
  FROZEN = 'frozen',
  BAKERY = 'bakery',
  BEVERAGES = 'beverages',
  OTHER = 'other'
}

// Store section display information
// Note: 'icon' field now contains Lucide icon name keys (e.g., 'produce', 'meat')
// These map to ICONS.shopping[iconKey] in the UI layer
export const STORE_SECTIONS: { [key in StoreSection]: { label: string; icon: string; order: number } } = {
  [StoreSection.PRODUCE]: { label: 'Produce', icon: 'produce', order: 1 },
  [StoreSection.MEAT]: { label: 'Meat & Poultry', icon: 'meat', order: 2 },
  [StoreSection.SEAFOOD]: { label: 'Seafood', icon: 'seafood', order: 3 },
  [StoreSection.DAIRY]: { label: 'Dairy & Eggs', icon: 'dairy', order: 4 },
  [StoreSection.PANTRY]: { label: 'Pantry & Dry Goods', icon: 'pantry', order: 5 },
  [StoreSection.SPICES]: { label: 'Spices & Seasonings', icon: 'spices', order: 6 },
  [StoreSection.FROZEN]: { label: 'Frozen', icon: 'frozen', order: 7 },
  [StoreSection.BAKERY]: { label: 'Bakery', icon: 'bakery', order: 8 },
  [StoreSection.BEVERAGES]: { label: 'Beverages', icon: 'beverages', order: 9 },
  [StoreSection.OTHER]: { label: 'Other', icon: 'other', order: 10 }
}

// Ingredient categorization mapping
export const INGREDIENT_CATEGORIES: { [key: string]: StoreSection } = {
  // Produce
  'lettuce': StoreSection.PRODUCE,
  'tomato': StoreSection.PRODUCE,
  'tomatoes': StoreSection.PRODUCE,
  'onion': StoreSection.PRODUCE,
  'onions': StoreSection.PRODUCE,
  'garlic': StoreSection.PRODUCE,
  'carrot': StoreSection.PRODUCE,
  'carrots': StoreSection.PRODUCE,
  'celery': StoreSection.PRODUCE,
  'potato': StoreSection.PRODUCE,
  'potatoes': StoreSection.PRODUCE,
  'bell pepper': StoreSection.PRODUCE,
  'bell peppers': StoreSection.PRODUCE,
  'broccoli': StoreSection.PRODUCE,
  'spinach': StoreSection.PRODUCE,
  'mushroom': StoreSection.PRODUCE,
  'mushrooms': StoreSection.PRODUCE,
  'avocado': StoreSection.PRODUCE,
  'lemon': StoreSection.PRODUCE,
  'lime': StoreSection.PRODUCE,
  'apple': StoreSection.PRODUCE,
  'banana': StoreSection.PRODUCE,
  'ginger': StoreSection.PRODUCE,
  'cilantro': StoreSection.PRODUCE,
  'parsley': StoreSection.PRODUCE,
  'basil': StoreSection.PRODUCE,
  'cucumber': StoreSection.PRODUCE,
  'zucchini': StoreSection.PRODUCE,
  
  // Meat & Poultry
  'chicken': StoreSection.MEAT,
  'chicken breast': StoreSection.MEAT,
  'chicken thigh': StoreSection.MEAT,
  'chicken thighs': StoreSection.MEAT,
  'ground beef': StoreSection.MEAT,
  'beef': StoreSection.MEAT,
  'pork': StoreSection.MEAT,
  'turkey': StoreSection.MEAT,
  'bacon': StoreSection.MEAT,
  'sausage': StoreSection.MEAT,
  'steak': StoreSection.MEAT,
  
  // Seafood
  'salmon': StoreSection.SEAFOOD,
  'fish': StoreSection.SEAFOOD,
  'shrimp': StoreSection.SEAFOOD,
  'cod': StoreSection.SEAFOOD,
  'tuna': StoreSection.SEAFOOD,
  
  // Dairy & Eggs
  'milk': StoreSection.DAIRY,
  'eggs': StoreSection.DAIRY,
  'egg': StoreSection.DAIRY,
  'cheese': StoreSection.DAIRY,
  'cheddar cheese': StoreSection.DAIRY,
  'mozzarella': StoreSection.DAIRY,
  'parmesan': StoreSection.DAIRY,
  'butter': StoreSection.DAIRY,
  'yogurt': StoreSection.DAIRY,
  'cream': StoreSection.DAIRY,
  'heavy cream': StoreSection.DAIRY,
  'sour cream': StoreSection.DAIRY,
  
  // Pantry & Dry Goods
  'rice': StoreSection.PANTRY,
  'pasta': StoreSection.PANTRY,
  'flour': StoreSection.PANTRY,
  'bread': StoreSection.PANTRY,
  'oats': StoreSection.PANTRY,
  'quinoa': StoreSection.PANTRY,
  'beans': StoreSection.PANTRY,
  'black beans': StoreSection.PANTRY,
  'chickpeas': StoreSection.PANTRY,
  'lentils': StoreSection.PANTRY,
  'oil': StoreSection.PANTRY,
  'olive oil': StoreSection.PANTRY,
  'vegetable oil': StoreSection.PANTRY,
  'vinegar': StoreSection.PANTRY,
  'balsamic vinegar': StoreSection.PANTRY,
  'soy sauce': StoreSection.PANTRY,
  'honey': StoreSection.PANTRY,
  'sugar': StoreSection.PANTRY,
  'brown sugar': StoreSection.PANTRY,
  'tomato sauce': StoreSection.PANTRY,
  'tomato paste': StoreSection.PANTRY,
  'coconut milk': StoreSection.PANTRY,
  'broth': StoreSection.PANTRY,
  'chicken broth': StoreSection.PANTRY,
  'vegetable broth': StoreSection.PANTRY,
  'nuts': StoreSection.PANTRY,
  'almonds': StoreSection.PANTRY,
  'walnuts': StoreSection.PANTRY,
  
  // Spices & Seasonings
  'salt': StoreSection.SPICES,
  'pepper': StoreSection.SPICES,
  'black pepper': StoreSection.SPICES,
  'paprika': StoreSection.SPICES,
  'cumin': StoreSection.SPICES,
  'oregano': StoreSection.SPICES,
  'thyme': StoreSection.SPICES,
  'rosemary': StoreSection.SPICES,
  'garlic powder': StoreSection.SPICES,
  'onion powder': StoreSection.SPICES,
  'chili powder': StoreSection.SPICES,
  'cayenne': StoreSection.SPICES,
  'cinnamon': StoreSection.SPICES,
  'vanilla': StoreSection.SPICES,
  'vanilla extract': StoreSection.SPICES,
  
  // Frozen
  'frozen vegetables': StoreSection.FROZEN,
  'frozen peas': StoreSection.FROZEN,
  'frozen corn': StoreSection.FROZEN,
  'ice cream': StoreSection.FROZEN,
  
  // Beverages
  'water': StoreSection.BEVERAGES,
  'juice': StoreSection.BEVERAGES,
  'coffee': StoreSection.BEVERAGES,
  'tea': StoreSection.BEVERAGES,
  'wine': StoreSection.BEVERAGES,
  'beer': StoreSection.BEVERAGES,
}

// Unit types for conversion system
export type VolumeUnit = 
  | 'ml' | 'l' | 'liter' | 'liters'
  | 'tsp' | 'teaspoon' | 'teaspoons'
  | 'tbsp' | 'tablespoon' | 'tablespoons'
  | 'cup' | 'cups'
  | 'pint' | 'pints'
  | 'quart' | 'quarts'
  | 'gallon' | 'gallons'
  | 'fl oz' | 'fluid ounce' | 'fluid ounces'

export type WeightUnit = 
  | 'g' | 'gram' | 'grams'
  | 'kg' | 'kilogram' | 'kilograms'
  | 'oz' | 'ounce' | 'ounces'
  | 'lb' | 'pound' | 'pounds'

export type CountUnit = 
  | 'item' | 'items'
  | 'piece' | 'pieces'
  | 'slice' | 'slices'
  | 'clove' | 'cloves'
  | 'head' | 'heads'
  | 'bunch' | 'bunches'
  | 'package' | 'packages'
  | 'can' | 'cans'
  | 'jar' | 'jars'
  | 'bottle' | 'bottles'

export type Unit = VolumeUnit | WeightUnit | CountUnit

export interface UnitConversionResult {
  success: boolean
  amount: number
  unit: string
  originalAmount1: number
  originalUnit1: string
  originalAmount2: number
  originalUnit2: string
  conversionNote?: string
}

// Shopping list generation options
export interface ShoppingListGenerationOptions {
  consolidateIngredients: boolean
  attemptUnitConversion: boolean
  includeRecipeNotes: boolean
  userPreferences?: {
    preferredUnits?: Unit[]
    hideCategories?: StoreSection[]
  }
}

// Default generation options
export const DEFAULT_GENERATION_OPTIONS: ShoppingListGenerationOptions = {
  consolidateIngredients: true,
  attemptUnitConversion: true,
  includeRecipeNotes: false,
}

// Shopping list sharing options
export interface ShoppingListShareData {
  title: string
  text: string
  url?: string
}